import { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Clock01IconStroke } from '@/components/icons';

import { T } from '../../util/Localize';
import { TextField } from '../../components/input';
import { isValidEmail } from '../../util/Functions';
import { useHost } from '../../hooks/use-host';
import { HUB_LOGIN_PATH } from '../../routes/paths';
import { requestPasswordReset } from '../../services/password-reset';
import { AuthPageLayout } from './auth-page-layout';
import { authSubmitButtonSx } from './auth-submit-button';

export default function PasswordResetRequest() {
    const location = useLocation();
    const prefillEmail = (location.state as { email?: string } | null)?.email ?? '';
    const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

    return (
        <AuthPageLayout>
            {submittedEmail ? (
                <PasswordResetSent email={submittedEmail} />
            ) : (
                <PasswordResetRequestForm initialEmail={prefillEmail} onSent={setSubmittedEmail} />
            )}
        </AuthPageLayout>
    );
}

function PasswordResetRequestForm({
    initialEmail,
    onSent
}: {
    initialEmail: string;
    onSent: (email: string) => void;
}) {
    const { data: hostInfo } = useHost();
    const [email, setEmail] = useState(initialEmail);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const emailValid = isValidEmail(email);

    const handleSubmit = async () => {
        if (!emailValid || submitting) return;
        if (!hostInfo?.brandId) {
            setError('Something went wrong. Please try again.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await requestPasswordReset({ email, brandId: hostInfo.brandId });
            onSent(email);
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={4}>
            <Box>
                <Typography sx={{ color: 'inherit', fontWeight: 900, fontSize: 36, lineHeight: 1 }}>
                    {T('Reset your password')}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1 }}>
                    {T("Enter the email on your account and we'll send a link to set a new one.")}
                </Typography>
            </Box>
            <Box
                component="form"
                display="flex"
                flexDirection="column"
                gap={1.25}
                onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    handleSubmit();
                }}
            >
                <TextField
                    type="email"
                    label={T('Email Address')}
                    value={email}
                    disabled={submitting}
                    error={email.length > 0 && !emailValid}
                    onChange={(e: any) => setEmail(e.target.value)}
                    labelSx={{ color: 'inherit' }}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                {error && (
                    <Typography sx={{ color: '#ff8a80', fontSize: 13 }}>{T(error)}</Typography>
                )}
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={!emailValid || submitting}
                    sx={authSubmitButtonSx}
                >
                    {submitting ? <CircularProgress size={22} sx={{ color: 'inherit' }} /> : T('Send Reset Link')}
                </Button>
                <Box display="flex" gap={0.75} justifyContent="center" sx={{ fontSize: 14 }}>
                    <Typography sx={{ color: 'inherit', fontSize: 'inherit' }}>{T('Remembered it?')}</Typography>
                    <RouterLink to={HUB_LOGIN_PATH} style={{ color: 'inherit', fontWeight: 700 }}>
                        {T('Back to Sign in')}
                    </RouterLink>
                </Box>
            </Box>
        </Box>
    );
}

function PasswordResetSent({ email }: { email: string }) {
    return (
        <Box display="flex" flexDirection="column" gap={3}>
            <Box>
                <Typography sx={{ color: 'inherit', fontWeight: 900, fontSize: 36, lineHeight: 1 }}>
                    {T('Password reset email')}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1.5 }}>
                    {T('We’ve sent a password reset email to')} {email}
                    {T('. It should arrive within the next couple of minutes.')}
                </Typography>
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1.5 }}>
                    {T("If you don't see it, check your spam or junk folder. It may have landed there.")}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'center',
                    p: 1.5,
                    border: '0.5px solid rgba(255,255,255,0.6)',
                    borderRadius: 1,
                    bgcolor: 'rgba(255,255,255,0.2)'
                }}
            >
                <HugeiconsIcon icon={Clock01IconStroke} strokeWidth={1.5} />
                <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 700 }}>
                    {T('The reset link is valid for 48 hours, so be sure to use it before it expires.')}
                </Typography>
            </Box>
            <Button component={RouterLink} to={HUB_LOGIN_PATH} variant="contained" fullWidth sx={{ py: 1 }}>
                {T('Back to Sign in')}
            </Button>
        </Box>
    );
}
